import { keyframes } from '@emotion/react';
import { Box, styled } from '@mui/material';
import { EColors, getColor } from 'theme';
import { Copy } from 'ui';

// Animations
const fadeInSpinner = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const fadeInOverlay = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

type SpinnerProps = {
    suppressMargin?: boolean;
    unsetMargin?: boolean;
    className?: string;
    delay?: number;
    size?: number;
};

const DEFAULT_SIZE = 40;
const MIN_STROKE_WIDTH = 3;
const STROKE_WIDTH_RATIO = 0.11;

const getStrokeWidth = (size: number) => Math.max(Math.round(size * STROKE_WIDTH_RATIO), MIN_STROKE_WIDTH);

const SpinnerWrapper = styled('div', {
    shouldForwardProp: prop => !['suppressMargin', 'unsetMargin', 'size', 'delay'].includes(prop as string),
})<SpinnerProps>(({ suppressMargin, unsetMargin, size = DEFAULT_SIZE, delay }) => ({
    position: 'relative',
    width: size,
    height: size,
    ...(unsetMargin ? {} : { margin: suppressMargin ? '0' : '20px auto' }),
    ...(delay && {
        animation: `${fadeInSpinner} 0.3s ${delay}s ease-in both`,
        opacity: 0,
    }),
}));

const SpinnerTrack = styled('div', {
    shouldForwardProp: prop => prop !== 'strokeWidth',
})<{ strokeWidth: number }>(({ strokeWidth }) => ({
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: `${strokeWidth}px solid ${getColor(EColors.v3Border)}`,
}));

const SpinnerArc = styled('div', {
    shouldForwardProp: prop => prop !== 'strokeWidth',
})<{ strokeWidth: number }>(({ strokeWidth }) => {
    const ringMask = `radial-gradient(farthest-side, transparent calc(100% - ${strokeWidth}px), #000 calc(100% - ${strokeWidth}px))`;

    return {
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        background: `conic-gradient(from 0deg, transparent 0deg, ${getColor(EColors.v3Dark)} 360deg)`,
        WebkitMask: ringMask,
        mask: ringMask,
        animation: `${spin} 0.85s linear infinite`,
    };
});

const BaseSpinner = ({ suppressMargin, unsetMargin, className, size = DEFAULT_SIZE, delay }: SpinnerProps) => (
    <SpinnerWrapper
        suppressMargin={suppressMargin}
        unsetMargin={unsetMargin}
        size={size}
        delay={delay}
        className={`bizly-spinner${className ? ` ${className}` : ''}`}
        role="status"
        aria-label="Loading"
    >
        <SpinnerTrack strokeWidth={getStrokeWidth(size)} />
        <SpinnerArc strokeWidth={getStrokeWidth(size)} />
    </SpinnerWrapper>
);

export const Spinner = BaseSpinner;

type SpinnerOverlayContainerProps = {
    fixed?: boolean;
    fixedSpinner?: boolean;
    transparent?: boolean;
    clickThrough?: boolean;
    delay?: number;
};

const SpinnerOverlayContainer = styled(Box, {
    shouldForwardProp: prop =>
        !['fixed', 'fixedSpinner', 'transparent', 'clickThrough', 'delay'].includes(prop as string),
})<SpinnerOverlayContainerProps>(({ fixed, fixedSpinner, transparent, clickThrough, delay }) => ({
    position: fixed ? 'fixed' : 'absolute',
    ...(fixed && { zIndex: 1300 }),
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getColor(EColors.pureWhite, transparent ? 0 : 0.8),
    animation: `${fadeInOverlay} 0.3s ${delay ?? 0}s ease-in both`,
    opacity: 0,
    ...(fixedSpinner && {
        '& > div:first-of-type': {
            position: 'fixed',
            top: '50vh',
            transform: 'translateY(-50%)',
        },
    }),
    ...(clickThrough && { pointerEvents: 'none' }),
}));

export const SpinnerOverlay = ({
    message,
    ...props
}: {
    className?: string;
    fixed?: boolean;
    fixedSpinner?: boolean;
    transparent?: boolean;
    clickThrough?: boolean;
    message?: string;
    delay?: number;
}) => (
    <SpinnerOverlayContainer {...props}>
        <BaseSpinner />
        {message && <Copy>{message}</Copy>}
    </SpinnerOverlayContainer>
);
